import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Paper,
  Divider,
  Grid,
  Stack,
} from "@mui/material";
import { useMutation } from "react-query";
import client from "../../../API";
import {
  dateToDatetimeLocal,
  invoiceStatuses,
  parseLocalFloat,
  parseLocalInt,
} from "../../../Utils/constants";

/**
 * Admin-only dialog to edit an invoice.
 * Adds support for:
 *   • Billed-To name & email (client_name / client_email)
 *   • Issuing company (custom_details.issuing_company)
 *   • Amount Paid  ➜ custom_details.amount_paid   ← NEW
 *   • Live preview that reflects all fields
 */
const InvoiceDialog = ({ open, onClose, invoiceInfo = undefined }) => {
  /* ------------------------------------------------------------------ */
  /* State                                                              */
  /* ------------------------------------------------------------------ */
  const [poNumber,      setPONumber]      = useState("");
  const [amountDue,     setAmountDue]     = useState("0.00");
  const [feePercentage, setFeePercentage] = useState("0");
  const [status,        setStatus]        = useState("");
  const [paymentTerm,   setPaymentTerm]   = useState(30);
  const [datePaid,      setDatePaid]      = useState(null);

  // custom_details fields
  const [billedToName,     setBilledToName]   = useState("");
  const [billedToEmail,    setBilledToEmail]  = useState("");
  const [issuingCompany,   setIssuingCompany] = useState("");
  const [amountPaid,       setAmountPaid]     = useState("");          // NEW

  /* ------------------------------------------------------------------ */
  /* Populate defaults when invoiceInfo loads                           */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!invoiceInfo) return;

    setPONumber(invoiceInfo.po_number            || "");
    setAmountDue(invoiceInfo.amount_due?.toFixed(2) || "0.00");
    setFeePercentage(invoiceInfo.fee_percentage?.toFixed(0) || "0");
    setStatus(invoiceInfo.status                 || "");
    setPaymentTerm(invoiceInfo.payment_term?.toString() || "30");

    setBilledToName(invoiceInfo.client_name                    || "");
    setBilledToEmail(invoiceInfo.client_email                  || "");
    setIssuingCompany(invoiceInfo.custom_details?.issuing_company || "");
    setAmountPaid(
      invoiceInfo.custom_details?.amount_paid?.toFixed(2) || ""        // NEW
    );

    setDatePaid(invoiceInfo.date_paid ? new Date(invoiceInfo.date_paid) : null);
  }, [invoiceInfo]);

  /* ------------------------------------------------------------------ */
  /* Helpers                                                            */
  /* ------------------------------------------------------------------ */
  const calculateAmountReceive = () => {
    try {
      const feeFloat = parseLocalFloat(feePercentage) || 0;
      const amtFloat = parseLocalFloat(amountDue)     || 0;
      return (amtFloat * (1 - feeFloat / 100)).toFixed(2);
    } catch {
      return "";
    }
  };
  const amountReceive = calculateAmountReceive();

  const handleDatePaidChange = (val) =>
    setDatePaid(val ? new Date(val) : null);

  /* ------------------------------------------------------------------ */
  /* API submission                                                     */
  /* ------------------------------------------------------------------ */
  const { mutate: editInvoiceAdmin, isLoading } = useMutation(
    client.invoices.editAdmin,
    {
      onSuccess: () => onClose(true),
      onError: (err) => {
        console.error(err);
        alert(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err.message
        );
      },
    }
  );

  const handleSubmit = () => {
    editInvoiceAdmin({
      id: invoiceInfo?.id,
      po_number:      poNumber,
      amount_due:     parseLocalFloat(amountDue),
      fee_percentage: parseLocalFloat(feePercentage),
      status,
      payment_term:   parseLocalInt(paymentTerm),
      date_paid:      datePaid,
      client_name:    billedToName,
      client_email:   billedToEmail,
      custom_details: {
        ...(invoiceInfo?.custom_details || {}),
        issuing_company: issuingCompany || undefined,
        amount_paid:     amountPaid !== "" ? parseLocalFloat(amountPaid) : undefined, // NEW
      },
    });
  };

  /* ------------------------------------------------------------------ */
  /* Derived preview values                                             */
  /* ------------------------------------------------------------------ */
  const displayPO            = poNumber         || "N/A";
  const displayAmountDue     = amountDue        || "0.00";
  const displayFeePercent    = feePercentage    || "0";
  const displayPaymentTerm   = paymentTerm      || "30";
  const displayStatus        = status           || "N/A";
  const displayDatePaid      = datePaid ? datePaid.toLocaleString() : "None";
  const displayAmountPaid    = amountPaid !== "" ? amountPaid : "—";        // NEW

  /* ------------------------------------------------------------------ */
  /* UI                                                                 */
  /* ------------------------------------------------------------------ */
  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="invoice-dialog-title"
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle id="invoice-dialog-title">Edit Invoice (Admin)</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
          {/* ----------------------------------------------------------- */}
          {/* LEFT: form                                                */}
          {/* ----------------------------------------------------------- */}
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <TextField
              label="PO Number"
              fullWidth
              margin="dense"
              value={poNumber}
              onChange={(e) => setPONumber(e.target.value)}
            />
            <TextField
              label="Amount Due"
              type="number"
              fullWidth
              margin="dense"
              value={amountDue}
              onChange={(e) => setAmountDue(e.target.value)}
            />
            <TextField
              label="Fee Percentage"
              type="number"
              fullWidth
              margin="dense"
              value={feePercentage}
              onChange={(e) => setFeePercentage(e.target.value)}
            />
            <TextField
              label={
                invoiceInfo?.invoice_type === "client"
                  ? "Amount user will receive"
                  : "Amount to collect"
              }
              value={amountReceive}
              fullWidth
              margin="dense"
              disabled
            />
            <TextField
              label="Status"
              select
              fullWidth
              margin="dense"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {invoiceStatuses.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Payment Term (days)"
              type="number"
              fullWidth
              margin="dense"
              value={paymentTerm}
              onChange={(e) => setPaymentTerm(e.target.value)}
            />
            <TextField
              label="Date Paid"
              type="datetime-local"
              fullWidth
              margin="dense"
              value={dateToDatetimeLocal(datePaid)}
              onChange={(e) => handleDatePaidChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            {/* custom_details */}
            <Divider sx={{ my: 2 }} />
            <TextField
              label="Billed-To Name"
              fullWidth
              margin="dense"
              value={billedToName}
              onChange={(e) => setBilledToName(e.target.value)}
            />
            <TextField
              label="Billed-To Email"
              type="email"
              fullWidth
              margin="dense"
              value={billedToEmail}
              onChange={(e) => setBilledToEmail(e.target.value)}
            />
            <TextField
              label="Issuing Company"
              fullWidth
              margin="dense"
              value={issuingCompany}
              onChange={(e) => setIssuingCompany(e.target.value)}
            />
            {/* ---- NEW field ---- */}
            <TextField
              label="Amount Paid"
              type="number"
              fullWidth
              margin="dense"
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
            />
          </Box>

          {/* ----------------------------------------------------------- */}
          {/* RIGHT: live preview                                        */}
          {/* ----------------------------------------------------------- */}
          <Box sx={{ flex: 1, borderLeft: "1px solid #ccc", pl: 3 }}>
            <Typography variant="h6" gutterBottom>
              Invoice Preview
            </Typography>
            <Paper elevation={3} sx={{ p: 4, background: "#fff", fontFamily: "Arial" }}>
              {/* header */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "#4a4e69" }}>
                    INVOICE
                  </Typography>
                  <Typography>{issuingCompany || "Blitz Systems Corp"}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Paper variant="outlined" sx={{ p: 2, background: "#f8f9fa" }}>
                    <Typography variant="body2" color="textSecondary">
                      Status
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {displayStatus}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* body */}
              <Stack spacing={1}>
                <KeyVal k="PO #"                v={displayPO} />
                <KeyVal k="Amount Due"          v={`$${displayAmountDue}`} />
                <KeyVal k="Fee (%)"             v={`${displayFeePercent}%`} />
                <KeyVal k="Payment Term"        v={`${displayPaymentTerm} days`} />
                <KeyVal k="Date Paid"           v={displayDatePaid} />
                <Divider sx={{ my: 1 }} />
                <KeyVal k="Billed-To"           v={`${billedToName || "—"} (${billedToEmail || "—"})`} />
                <KeyVal k="Amount Paid"         v={displayAmountPaid} />     {/* NEW */}
                <KeyVal k="Issuing Co."         v={issuingCompany || "Blitz Systems Corp"} />
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="body2">
                <strong>Amount Received / Net:</strong> ${amountReceive}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button disabled={isLoading} onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button disabled={isLoading} onClick={handleSubmit}>
          {isLoading ? "Submitting…" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* helper for preview rows */
const KeyVal = ({ k, v }) => (
  <Stack direction="row" spacing={1}>
    <Typography variant="body2" color="textSecondary">
      {k}:
    </Typography>
    <Typography variant="body2">{v}</Typography>
  </Stack>
);

export default InvoiceDialog;
